import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
    ClockCircleOutlined,
    ClearOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Table,
    Tag,
    message,
    Modal,
    Input,
    Space,
    Tooltip,
    Select,
    DatePicker,
    Row,
    Col,
    Typography,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

export default function MyStatusRequests() {
    const { authData, backUrl } = useContext(AuthContext);
    const isMobile = window.innerWidth < 768;

    // State management
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // ✅ Filter reference data
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // ✅ Filter states
    const [employeeNameFilter, setEmployeeNameFilter] = useState("");
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedDesignations, setSelectedDesignations] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [requestStatusFilter, setRequestStatusFilter] = useState(null); // null | "pending" | "approved" | "rejected"

    // Modal state (kept for parity; MyStatusRequests is read-only today but left in case actions are added later)
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState("");
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Status color mapping (attendance status tags)
    const getStatusColor = (status) => {
        const statusColors = {
            "PRESENT": "green",
            "PERMISSION": "orange",
            "ABSENT": "red",
            "HOLIDAY": "blue",
            "Weekoff": "purple",
            "Compoff": "cyan",
            "CASUAL LEAVE": "magenta",
            "SICK LEAVE": "volcano",
            "HALFDAY_LEAVE": "geekblue",
        };
        return statusColors[status] || "default";
    };

    // ✅ Derive a record's request status (pending/approved/rejected) from backend fields
    const getRequestStatus = (record) => {
        if (record.is_approval_pending) return "pending";
        const msg = (record.status_message || "").toUpperCase();
        if (msg.includes("APPROVED")) return "approved";
        if (msg.includes("REJECTED")) return "rejected";
        return "pending"; // safe fallback if fields are missing
    };

    const requestStatusTagConfig = {
        pending: { color: "gold", label: "PENDING" },
        approved: { color: "green", label: "APPROVED" },
        rejected: { color: "red", label: "REJECTED" },
    };

    // ✅ Fetch reference data for department/designation dropdowns
    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDepartments(res.data.results || []);
        } catch (err) {
            console.error("Failed to load departments");
        }
    };

    const fetchDesignations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDesignations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load designations");
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchDesignations();
    }, []);

    // Fetch requests with all filters applied
    const fetchPendingRequests = async () => {
        setLoading(true);
        try {
            const url = `${backUrl}/api/hr/attendance-changes/my-requests/`;

            const params = {
                page: currentPage,
                page_size: pageSize,
            };

            if (employeeNameFilter.trim()) params.employee_name = employeeNameFilter.trim();
            if (empCodeFilter.trim()) params.emp_code = empCodeFilter.trim();
            if (selectedDepartments.length > 0) params.department = selectedDepartments;
            if (selectedDesignations.length > 0) params.designation = selectedDesignations;
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.from_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
                params.to_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
            }
            if (requestStatusFilter) params.req_status = requestStatusFilter;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
                params,
                paramsSerializer: {
                    indexes: null, // sends department=1&department=2 style for multi-select arrays
                },
            });

            const responseData = res.data;

            // Handle both paginated ({results, count}) and current ({data, total_count}) shapes
            const records = responseData.results || responseData.data || [];
            const total = responseData.count ?? responseData.total_count ?? records.length;

            setDataSource(records);
            setTotalRecords(total);
        } catch (error) {
            console.error("Error fetching pending requests:", error);
            message.error("Failed to fetch status change requests");
            setDataSource([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Refetch whenever page, page size, or any filter changes
    useEffect(() => {
        fetchPendingRequests();
    }, [
        currentPage,
        pageSize,
        employeeNameFilter,
        empCodeFilter,
        selectedDepartments,
        selectedDesignations,
        dateRange,
        requestStatusFilter,
    ]);

    // Handle table pagination change
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // ✅ Filter change handlers (each resets to page 1)
    const handleEmployeeNameChange = (e) => {
        setEmployeeNameFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleEmpCodeChange = (e) => {
        setEmpCodeFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleDepartmentChange = (value) => {
        setSelectedDepartments(value);
        setCurrentPage(1);
    };

    const handleDesignationChange = (value) => {
        setSelectedDesignations(value);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates || [null, null]);
        setCurrentPage(1);
    };

    const handleRequestStatusChange = (value) => {
        setRequestStatusFilter(value);
        setCurrentPage(1);
    };

    const handleClearAllFilters = () => {
        setEmployeeNameFilter("");
        setEmpCodeFilter("");
        setSelectedDepartments([]);
        setSelectedDesignations([]);
        setDateRange([null, null]);
        setRequestStatusFilter(null);
        setCurrentPage(1);
    };

    const hasActiveFilters = () => {
        return (
            employeeNameFilter.trim() ||
            empCodeFilter.trim() ||
            selectedDepartments.length > 0 ||
            selectedDesignations.length > 0 ||
            (dateRange && dateRange[0] && dateRange[1]) ||
            requestStatusFilter
        );
    };

    // Table columns definition
    const columns = [
        {
            title: 'Requested By',
            dataIndex: "requested_by_name",
            key: 'requested_by'
        },
        {
            title: "Employee Code",
            dataIndex: "emp_code",
            key: "emp_code",
            width: 180,
        },
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
            width: 180,
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department",
            width: 150,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation",
            width: 150,
        },
        {
            title: "Attendance Date",
            dataIndex: "date",
            key: "date",
            width: 130,
        },
        {
            title: "Current Status",
            dataIndex: "status",
            key: "current_status",
            width: 130,
        },
        {
            title: "Requested Status",
            dataIndex: "pending_status_change",
            key: "new_status",
        },
        {
            title: "Remarks",
            dataIndex: "status_change_remarks",
            key: "remarks",
            width: 200,
        },
        // ✅ New: Request Status column (Pending / Approved / Rejected)
        {
            title: "Request Status",
            key: "request_status",
            width: 150,
            render: (_, record) => {
                const statusKey = getRequestStatus(record);
                const config = requestStatusTagConfig[statusKey];
                return <Tag color={config.color}>{config.label}</Tag>;
            },
        },
    ];

    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        Status Change Requests
                    </p>
                ) : (
                    <h5>
                        Attendance Status Change Requests
                    </h5>
                )}
            </center>

            {/* ✅ Filter Section */}
            <div style={{ margin: "0 20px" }}>
                <Card style={{ marginBottom: "16px" }}>
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Employee Name</Text>
                                <Input
                                    placeholder="Search by name"
                                    value={employeeNameFilter}
                                    onChange={handleEmployeeNameChange}
                                    allowClear
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Employee Code</Text>
                                <Input
                                    placeholder="Search by code"
                                    value={empCodeFilter}
                                    onChange={handleEmpCodeChange}
                                    allowClear
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Department</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select departments"
                                    value={selectedDepartments}
                                    onChange={handleDepartmentChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {departments.map((dept) => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Designation</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select designations"
                                    value={selectedDesignations}
                                    onChange={handleDesignationChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {designations.map((desg) => (
                                        <Option key={desg.id} value={desg.id}>
                                            {desg.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Request Status</Text>
                                <Select
                                    placeholder="All statuses"
                                    value={requestStatusFilter}
                                    onChange={handleRequestStatusChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                >
                                    <Option value="pending">
                                        <Tag color="gold" style={{ marginRight: 0 }}>Pending</Tag>
                                    </Option>
                                    <Option value="approved">
                                        <Tag color="green" style={{ marginRight: 0 }}>Approved</Tag>
                                    </Option>
                                    <Option value="rejected">
                                        <Tag color="red" style={{ marginRight: 0 }}>Rejected</Tag>
                                    </Option>
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Attendance Date Range</Text>
                                <RangePicker
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                    format="DD-MM-YYYY"
                                    placeholder={["Start Date", "End Date"]}
                                    style={{ width: "100%" }}
                                />
                            </Space>
                        </Col>

                        {hasActiveFilters() && (
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <Button
                                    icon={<ClearOutlined />}
                                    onClick={handleClearAllFilters}
                                    style={{ width: "100%" }}
                                >
                                    Clear Filters
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Card>
            </div>

            {/* Requests Table */}
            <div style={{ margin: "20px" }}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) => record.id}
                    onChange={handleTableChange}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>
        </section>
    );
}